import React, { useEffect, useRef, useState } from "react";
import { SareeHalf, useTicketAd } from "../components/HalfA4Ticket";
import { SareeSlip } from "../components/CardSlips";
import PrintSizeSelect, { usePrintSize, usePrintColors } from "../components/PrintSizeSelect";
import DirectPrintBanner from "../components/DirectPrintBanner";
import api, { fmtErr } from "../lib/api";
import { printHtml } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import SareeCard from "../components/SareeCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Printer, RotateCcw, Shirt, Camera } from "lucide-react";
import { toast } from "sonner";

export default function SareeRequest() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templeName, setTempleName] = useState("");
  const [form, setForm] = useState({ category: "", saree_type_id: "", rate: "", quantity: 1, phone: "", name: "", description: "", booking_date: "", return_type: "" });
  const [photo, setPhoto] = useState(null);
  const [templeLoc, setTempleLoc] = useState("");
  const [temple, setTemple] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const cardRef = useRef(null);
  const printSize = usePrintSize();
  const portrait = printSize === "4x6";
  const lightPrint = usePrintColors() === "light";
  const halfTicket = ["half", "a5", "s21x15"].includes(printSize); // half-A4 ticket design
  const ticketAd = useTicketAd(user?.temple_id, halfTicket);

  useEffect(() => {
    api.get("/saree-types").then((r) => setTypes(r.data.filter((t) => t.status === "ACTIVE"))).catch(() => {});
    api.get("/saree-categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/temples").then((r) => {
      const t = r.data.find((x) => x.temple_id === user.temple_id) || {};
      setTempleName(t.name || "");
      setTempleLoc(t.location || "");
      setTemple(t);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [avail, setAvail] = useState(null);
  const selType = types.find((t) => t.id === form.saree_type_id);
  useEffect(() => {
    if (!selType?.date_quota) { setAvail(null); return; }
    const q = new URLSearchParams({ saree_type_id: selType.id });
    if (form.booking_date) q.set("date", form.booking_date);
    api.get(`/saree-date-quotas/availability?${q}`).then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [selType?.id, selType?.date_quota, form.booking_date, result]); // eslint-disable-line
  const dmy = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "");
  const quotaBlocked = !!selType?.date_quota && (!form.booking_date || !avail || avail.remaining < (Number(form.quantity) || 1));
  const onType = (id) => {
    const st = types.find((t) => t.id === id);
    // Booking Date applies only to date-wise quota types (e.g. Pattu Saree).
    setForm((f) => ({ ...f, saree_type_id: id, booking_date: st?.date_quota ? f.booking_date : "" }));
  };
  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2.5 * 1024 * 1024) { toast.error("Photo too large (max 2.5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640 } });
      streamRef.current = stream;
      setCameraOn(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch {
      toast.error("Camera not available. Use photo upload instead.");
    }
  };
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };
  useEffect(() => () => stopCamera(), []);
  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = Math.round((video.videoHeight / video.videoWidth) * 480) || 480;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL("image/jpeg", 0.7));
    stopCamera();
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/sarees/token", { ...form, rate: Number(form.rate) || 0, quantity: Number(form.quantity) || 1, photo });
      setResult(r.data);
      toast.success(`Receiving token ${r.data.token_no} created — printing…`);
      setTimeout(() => { if (cardRef.current) printHtml(cardRef.current.outerHTML, "ticket-page", `Saree_Token_${r.data.token_no}`); }, 500);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setResult(null); setForm({ category: "", saree_type_id: "", rate: "", quantity: 1, phone: "", name: "", description: "", booking_date: "", return_type: "" }); setPhoto(null); };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="saree-success">
        <Card className="card-gold-top shadow-lg text-center">
          <CardContent className="p-6 space-y-2">
            <Shirt className="w-12 h-12 text-maroon-700 mx-auto" />
            <h2 className="font-heading text-2xl font-bold text-maroon-900">Receiving Token Created</h2>
            <p className="font-mono text-xl font-bold text-maroon-900" data-testid="saree-success-token">{result.token_no}</p>
            <p className="text-sm text-slate-500">{result.category} · {result.saree_type_name} · ₹{result.rate}</p>
            <div className="flex gap-2 justify-center pt-2">
              <Button data-testid="saree-print-btn" onClick={() => cardRef.current && printHtml(cardRef.current.outerHTML, "ticket-page", `Saree_Token_${result.token_no}`)}
                className="bg-saffron-500 hover:bg-saffron-600 text-white"><Printer className="w-4 h-4 mr-1" /> Print Again</Button>
              <Button variant="outline" data-testid="saree-new-btn" onClick={reset}><RotateCcw className="w-4 h-4 mr-1" /> New Token</Button>
            </div>
            <div className="pt-1"><PrintSizeSelect /></div>
          </CardContent>
        </Card>
        <div className={`overflow-x-auto flex flex-col gap-4 ${lightPrint ? "print-light" : ""}`} ref={cardRef}>
          {/* Two copies: one for the devotee, one kept at the office (one A4 sheet, or one card each). */}
          {halfTicket ? (
            <>
              <SareeHalf token={result} temple={temple} templeName={templeName} templeLocation={templeLoc} copyLabel="Devotee Copy" ad={ticketAd} />
              <SareeHalf token={result} temple={temple} templeName={templeName} templeLocation={templeLoc} copyLabel="Office Copy" ad={ticketAd} />
            </>
          ) : printSize === "slip" ? (
            <>
              <SareeSlip token={result} temple={temple} copyLabel="Devotee Copy" />
              <SareeSlip token={result} temple={temple} copyLabel="Office Copy" />
            </>
          ) : (
            <>
              <SareeCard token={result} temple={temple} templeName={templeName} templeLocation={templeLoc} copyLabel="Devotee Copy" portrait={portrait} />
              <SareeCard token={result} temple={temple} templeName={templeName} templeLocation={templeLoc} copyLabel="Office Copy" portrait={portrait} />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="saree-request-page">
      <DirectPrintBanner />
      <div className="flex justify-end mb-3"><PrintSizeSelect /></div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Sarees — Receiving Counter</h1>
      <p className="text-sm text-slate-500 mb-5">Issue a saree request token. The access card prints automatically — no approval required.</p>
      <form onSubmit={submit}>
        <Card className="card-gold-top shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Request Token</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="saree-category-select"><SelectValue placeholder="Select Category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Saree Type *</Label>
              <Select value={form.saree_type_id} onValueChange={onType}>
                <SelectTrigger data-testid="saree-type-select"><SelectValue placeholder="Select Saree Type" /></SelectTrigger>
                <SelectContent>{types.filter((t) => !user.temple_id || t.temple_id === user.temple_id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{user.temple_id ? "" : ` · ${t.temple_id}`}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rate (₹) — manual entry *</Label>
              <Input data-testid="saree-rate" type="number" min={0} step="0.01" required value={form.rate} onChange={(e) => set("rate", e.target.value)} placeholder="Enter rate" />
            </div>
            <div><Label>Quantity</Label><Input data-testid="saree-quantity" type="number" min={1} max={1000} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></div>
            <div>
              <Label>Return Type</Label>
              <Select value={form.return_type} onValueChange={(v) => set("return_type", v)}>
                <SelectTrigger data-testid="saree-return-type"><SelectValue placeholder="Select Return Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Returnable">Returnable</SelectItem>
                  <SelectItem value="Non-Returnable">Non-Returnable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selType?.date_quota && <div><Label>Booking Date *</Label><Input data-testid="saree-booking-date" type="date" value={form.booking_date} onChange={(e) => set("booking_date", e.target.value)} />
              {selType?.date_quota && avail && (
                <div className="mt-1 text-xs" data-testid="saree-quota-status">
                  {!form.booking_date ? <span className="text-slate-600">{selType.name} is released date-wise — select a booking date.</span>
                    : avail.status === "AVAILABLE" ? <span className="text-green-700 font-semibold">Available: {avail.remaining} of {avail.quota} for {dmy(form.booking_date)}</span>
                    : <span className="text-red-700 font-semibold">{avail.status === "QUOTA_FULL" ? `Quota Full for ${dmy(form.booking_date)}. Please select the next available date.` : `Quota not released for ${dmy(form.booking_date)}.`}</span>}
                  {avail.next_available && (!form.booking_date || avail.status !== "AVAILABLE") && (
                    <button type="button" className="ml-2 underline text-maroon-700" onClick={() => set("booking_date", avail.next_available)} data-testid="saree-next-date">
                      Use next available: {dmy(avail.next_available)}</button>)}
                </div>)}
            </div>}
            <div className="sm:col-span-2">
              <Label>Saree Photo</Label>
              {cameraOn ? (
                <div className="space-y-2 mt-1">
                  <div className="relative rounded-md overflow-hidden border-2 border-gold-400 max-w-sm">
                    <video ref={videoRef} autoPlay playsInline className="w-full" data-testid="saree-camera-feed" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" onClick={capture} className="bg-saffron-500 hover:bg-saffron-600 text-white" data-testid="saree-capture-btn">
                      <Camera className="w-4 h-4 mr-1" /> Capture Photo
                    </Button>
                    <Button type="button" variant="outline" onClick={stopCamera} data-testid="saree-camera-cancel-btn">Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap mt-1">
                  {photo && <img src={photo} alt="saree" className="w-16 h-16 object-cover rounded border border-gold-400" data-testid="saree-photo-preview" />}
                  <Button type="button" variant="outline" onClick={startCamera} data-testid="saree-camera-btn">
                    <Camera className="w-4 h-4 mr-1" /> {photo ? "Retake (Live Camera)" : "Live Camera"}
                  </Button>
                  <label className="text-sm text-maroon-700 underline cursor-pointer" data-testid="saree-photo-upload">
                    {photo ? "or replace with upload" : "or upload photo"}
                    <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                  </label>
                </div>
              )}
            </div>
            <div><Label>Phone No</Label><Input data-testid="saree-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} pattern="[0-9]{10}" title="10-digit mobile number" /></div>
            <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="saree-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Devotee / contractor name" /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="saree-description" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <p className="sm:col-span-2 text-xs text-slate-500">Counter User: <span className="font-semibold text-maroon-800">{user.full_name}</span></p>
          </CardContent>
        </Card>
        <div className="flex gap-2 mt-5">
          <Button type="button" variant="outline" onClick={reset} data-testid="saree-reset-btn" className="h-12"><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
          <Button type="submit" disabled={loading || quotaBlocked} data-testid="saree-submit-btn"
            className="flex-1 h-12 bg-maroon-700 hover:bg-maroon-800 text-gold-100 font-heading tracking-widest text-base">
            {loading ? "ISSUING…" : "PRINT REQUEST TOKEN"}
          </Button>
        </div>
      </form>
    </div>
  );
}
